import { Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomSwitch from "../../../components/CustomSwitch";
import {
  createBranchDataSharing,
  getBranchesDataSharing,
  updateBranchDataSharing,
} from "../../../redux/reducers/clinicSlice";
import { mergeMandateSessionKeepTimestamp } from "../../../util/mandateSessionStorage";
import "../BranchManagement/BranchManagement.css";

const initValues = {
  petData: false,
  vetData: false,
  planData: false,
  globalSettingsData: false,
  inventryData: false,
  serviceData: false,
  allowBranchStockEdit: false,
  allowBranchServiceEdit: false,
};

const sharingRows = [
  {
    key: "petData",
    title: "Pets Data",
    description: "Allow pet data to be shared across all branches.",
  },
  {
    key: "vetData",
    title: "Vets Data",
    description: "Allow vet data to be shared across all branches.",
  },
  {
    key: "planData",
    title: "Plan Data",
    description: "Allow plan data to be shared across all branches.",
  },
  {
    key: "globalSettingsData",
    title: "Global Settings",
    description: "Allow global settings to be shared across all branches.",
  },
  {
    key: "inventryData",
    title: "Inventory Data — Stock",
    description: "Allow inventory stock data to be shared across all branches.",
  },
  {
    key: "allowBranchStockEdit",
    title: "Allow branches to create and edit Stock",
    description:
      "When enabled, branches can create and edit stock items. Changes will be available across all associated branches.",
  },
  {
    key: "serviceData",
    title: "Inventory Data — Services",
    description:
      "Allow inventory services data to be shared across all branches.",
  },
  {
    key: "allowBranchServiceEdit",
    title: "Allow branches to create and edit Services",
    description:
      "When enabled, branches can create and edit services. Changes will be available across all associated branches.",
  },
];

const GeneralDataSharing = () => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initValues);
  const { branchesDataSharing } = useSelector((state) => state?.clinic);
  const user = localStorage.getItem("user");
  let profile = {};
  try {
    profile = user ? JSON.parse(user) : {};
  } catch {
    profile = {};
  }

  useEffect(() => {
    onApiCall();
  }, []);

  useEffect(() => {
    const matchedRecord = recMatchedWithCurrUser() ?? null;
    if (matchedRecord) {
      setValues({
        petData: matchedRecord?.petData ?? false,
        vetData: matchedRecord?.vetData ?? false,
        planData: matchedRecord?.planData ?? false,
        globalSettingsData: matchedRecord?.globalSettingsData ?? false,
        inventryData: matchedRecord?.inventryData ?? false,
        serviceData: matchedRecord?.serviceData ?? false,
        allowBranchStockEdit: matchedRecord?.allowBranchStockEdit ?? false,
        allowBranchServiceEdit: matchedRecord?.allowBranchServiceEdit ?? false,
      });
    }
  }, [branchesDataSharing]);

  const onApiCall = () => dispatch(getBranchesDataSharing());

  const recMatchedWithCurrUser = () => {
    const list = Array.isArray(branchesDataSharing) ? branchesDataSharing : [];
    if (list.length === 0) return null;
    const matchedRec = list.find((li) => li?.createdBy === profile?.id);
    return matchedRec ? matchedRec : list?.[0];
  };

  const onChangeSwitchValues = (name, value) => {
    const next = { ...values, [name]: value };
    if (name === "inventryData" && !value) next.allowBranchStockEdit = false;
    if (name === "serviceData" && !value) next.allowBranchServiceEdit = false;
    setValues(next);
  };

  const visibleSharingRows = sharingRows.filter((row) => {
    if (row.key === "allowBranchStockEdit") return !!values.inventryData;
    if (row.key === "allowBranchServiceEdit") return !!values.serviceData;
    return true;
  });

  const patchDataSharingFromResponse = (payload) => {
    try {
      if (payload == null) return;
      const sharing = Array.isArray(payload)
        ? (payload?.[0] ?? {})
        : payload && typeof payload === "object"
          ? payload
          : {};
      mergeMandateSessionKeepTimestamp({
        dataSharing: sharing,
      });
    } catch {
      // ignore mandate patch errors
    }
  };

  const refreshDataSharingAndPatchMandate = () => {
    try {
      onApiCall().then((res) => {
        if (res?.payload != null) {
          patchDataSharingFromResponse(res.payload);
        }
      });
    } catch {
      // ignore dataSharing refresh/patch errors
    }
  };

  const onSaveChanges = () => {
    try {
      const matchedRecord = recMatchedWithCurrUser() ?? null;
      const reqObj = { data: values ?? initValues };
      if (matchedRecord?.shareId) {
        reqObj.id = matchedRecord.shareId;
      }
      dispatch(
        matchedRecord?.shareId
          ? updateBranchDataSharing(reqObj)
          : createBranchDataSharing(reqObj),
      ).then((res) => {
        if (res?.payload) {
          refreshDataSharingAndPatchMandate();
        }
      });
    } catch {
      // ignore save errors
    }
  };

  return (
    <div className="consultation-white-con scroll-80vh p20">
      <div className="flex-row-between-align-center mb20 pb10">
        <div>
          <Typography className="text-bold fs20 black">
            General & Data Sharing
          </Typography>
          <Typography className="txt-regular fs13 card-gray-color mt4">
            Control how data is shared between this clinic&apos;s branches.
          </Typography>
        </div>
        <div className="flex-row-ali-cen gap10">
          <CustomButton text="Save Changes" onClick={onSaveChanges} smallBtn />
        </div>
      </div>
      <Divider />

      <div className="back-white br12 border p20">
        {visibleSharingRows?.map((row, index) => {
          const isLast = index === visibleSharingRows?.length - 1;
          const checked = !!values?.[row.key];
          return (
            <div key={row.key} className="mt10">
              <div className="flex-row-between-align-center py15">
                <div>
                  <Typography className="text-bold fs14 black">
                    {row.title}
                  </Typography>
                  <Typography className="txt-regular fs12 card-gray-color mt2">
                    {row.description}
                  </Typography>
                </div>
                <CustomSwitch
                  value={checked}
                  onChange={() => onChangeSwitchValues(row.key, !checked)}
                  grayToBlue
                />
              </div>
              {!isLast ? <Divider /> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GeneralDataSharing;
